import { useEffect } from 'react';
import { SinglePaymentParameters, PaymentDetailResult } from '@mews-commander/data-access';
import { ContentContainer, ModalBodyWrapper, ModalWrapper, AsyncWrapper } from '@mews-commander/ui';
import { ActionType, TextKey } from '@mews-commander/enums';
import { LocalizedText } from '@mews/framework';
import { IconName } from '@optimus-web/icons';
import { Card, Grid, GridItem, Stack } from '@optimus-web/core';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import ModalHeader from '../../../../../legacy/React/Components/Common/ModalHeader';
import { useCloseButton } from '../../../../../core/windowManager/hooks';
import { HeadingAction } from '../../../../../legacy/React/Components/HeadingAction/Action/types';
import { changeDocumentTitle } from '../../../../../middleware';
import { getPaymentDetailsEntries, renderDetailEntries, renderReceiptEntries } from './components/legacy/util';
import { useGetPaymentDetails } from './queries';
import { usePrintSettings } from './hooks';

interface PaymentDetailContentProps {
    data: PaymentDetailResult;
    paymentId: SinglePaymentParameters['paymentId'];
}

const PaymentDetailContent = ({ data: { details, receipt }, paymentId }: PaymentDetailContentProps) => {
    const dispatch = useCommanderThunkDispatch();
    const closeButton = useCloseButton();
    const printSettings = usePrintSettings({ paymentId });

    const { title } = details;
    const actions: HeadingAction[] = [
        {
            ...printSettings,
            type: ActionType.Print,
            text: <LocalizedText textKey={TextKey.Print} />,
            icon: IconName.Print,
            disabled: false,
        },
    ];

    useEffect(() => {
        dispatch(changeDocumentTitle(title));
    }, [dispatch, title]);

    return (
        <>
            <ModalHeader title={title} primaryAction={closeButton} actions={actions} />
            <ModalBodyWrapper>
                <ContentContainer condensed>
                    <Grid templateColumns="1fr 3fr">
                        <GridItem>
                            <Card padding="200">
                                <Stack vertical spacing="100">
                                    {renderDetailEntries(getPaymentDetailsEntries(details))}
                                </Stack>
                            </Card>
                        </GridItem>
                        {receipt && (
                            <GridItem>
                                <Card padding="200">
                                    <Stack vertical spacing="100">
                                        {renderReceiptEntries(receipt.receiptData)}
                                    </Stack>
                                </Card>
                            </GridItem>
                        )}
                    </Grid>
                </ContentContainer>
            </ModalBodyWrapper>
        </>
    );
};

export const PaymentDetails = ({ paymentId }: Pick<SinglePaymentParameters, 'paymentId'>) => {
    const { data, isFetching, isError } = useGetPaymentDetails({ paymentId });

    return (
        <ModalWrapper>
            <AsyncWrapper isFetching={isFetching} hasFailed={isError}>
                {data && <PaymentDetailContent paymentId={paymentId} data={data} />}
            </AsyncWrapper>
        </ModalWrapper>
    );
};
